import type { SessionPlayerPayload } from "@cwb/protocol";
import type { PortalBridge } from "./portalBridge";

export interface PlayerForwarder {
  /** Send the current assertion now. Call after every (re)join of the session. */
  sync(): void;
  dispose(): void;
}

/**
 * Carry the portal's player assertion to the game server for one session.
 *
 * It sends on every join / reconnect (a reconnect may land on a socket that
 * never saw it) and again whenever the portal changes it — including to `null`,
 * which is how a logout or a withdrawn identity reaches the server before the
 * run ends.
 *
 * Standalone, the bridge is inactive and this sends nothing, so a guest game
 * behaves exactly as it did before the portal existed.
 */
export function createPlayerForwarder(
  bridge: PortalBridge,
  sessionId: string,
  send: (payload: SessionPlayerPayload) => void,
  isConnected: () => boolean,
): PlayerForwarder {
  const push = (): void => {
    if (!bridge.active || !isConnected()) return;
    send({ sessionId, assertion: bridge.current() });
  };

  const unsubscribe = bridge.subscribe(push);

  return {
    sync() {
      if (!bridge.active || !isConnected()) return;
      // If the held assertion is about to lapse, or the portal has not said who
      // is playing yet, ask first; the reply arrives as a change and is
      // forwarded by the subscription.
      bridge.refreshIfExpiring();
      bridge.requestIfMissing();
      push();
    },
    dispose: unsubscribe,
  };
}
